import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  ChartTooltip,
  type ChartTooltipComparison,
} from '../../molecules/ChartTooltip/ChartTooltip';
import { token, type TokenName } from '../../tokens/tokens';
import styles from './WordCloud.module.css';

/**
 * A word cloud: words sized by how much of something each one carries.
 *
 * **Two encodings, and they answer different questions.** Size is volume — the
 * more mentions a word has, the bigger it is drawn, which is the whole of what
 * a cloud is for. Colour is *tone*: the sentiment that most of that word's
 * mentions carry, taken from the same `chart/4` / `chart/5` / `chart/6` triple
 * every sentiment mark in this app uses. So a large red word is a lot of angry
 * conversation and a small green one is a quiet, happy corner, and neither
 * reading needs the legend to be re-learned.
 *
 * Carried over from the DeepDive v4 prototype's `TagCloud`, then taken past it.
 * The prototype declined a real layout — *"no rotation, no packing algorithm…
 * it reads the way a cloud does without pretending to be a d3 layout it
 * isn't"* — and this component was a `flex-wrap` on that basis until
 * **2026-09-26, when it got the packing** (asked for against a reference
 * cloud). Flex could not draw the shape at all: every word on a line shares a
 * line box as tall as the biggest word on it, so the space above and below a
 * small word is unreachable and the cloud reads as a ragged paragraph. See
 * `packed`, which places each word at its own x and y.
 *
 * Still no rotation: every word is horizontal, which is what the reference
 * shows and what keeps a Bangla label legible.
 *
 * Three things the prototype did that this does not:
 *
 *  1. **Colour is not `CH[i % 15]`.** The prototype walked the chart ramp by
 *     position, so a word's colour meant nothing at all. Here it means tone.
 *     Settled with the user (2026-09-17); see `dominantSentiment`.
 *  2. **Size is quantised to the type scale, not a continuous 13→40px ramp.**
 *     Stylelint refuses a literal px on `font-size`, and it is right to: a
 *     continuous ramp mints five sizes this system does not define. Five steps
 *     off `font-size/xs … display-xs` is the same move `CalendarHeatmap` made
 *     when it took equal fifths of one token rather than a hand-mixed ramp.
 *  3. **Each word is a real `<button>`** where the prototype used a `<span>`
 *     with a `title`. A mark carrying a readout has to be reachable by keyboard
 *     — flagged as an addition beyond the design, like the focus rings.
 *
 * **Not in Figma.** The frame (951:20841) leaves both cloud cards' `Chart`
 * slots empty, exactly as it leaves the other two. This follows `ScatterChart`
 * and `CalendarHeatmap`'s precedent rather than the spectrum-ui import's
 * three-step one: no component set, no `parity.expected.json`, until a node
 * exists to measure against. Every value still resolves to a token.
 *
 * It does not use `ChartFrame` — that wraps its children in Recharts'
 * `ResponsiveContainer`, and there is nothing here for Recharts to measure. It
 * mirrors the frame's structure instead: plot box, `role="img"`, the same two
 * replaced states, a legend slot underneath.
 *
 * ```tsx
 * <WordCloud words={topics} unit="mentions" />
 * ```
 */
export type WordCloudWord = {
  /** Stable identity. Two words may share `label`; they may not share this. */
  key: string;
  /** What is drawn. */
  label: string;
  /** What sizes it. */
  value: number;
  /** What colours it. Omit for the neutral fallback — see `.untoned`. */
  color?: TokenName;
  /**
   * Named in the readout and the accessible name, under the value — `Positive`.
   * Without it a coloured word is a colour nobody can name.
   */
  tone?: string;
  /**
   * A second line in the readout — `62% of its mentions`. The share behind the
   * tone, which is what says whether the colour was a landslide or a plurality
   * of forty percent.
   */
  detail?: string;
  /**
   * Spoken form, where the label is not in the document's language. The keyword
   * cloud is Bangla and an English-language screen reader announcing
   * `নেটওয়ার্ক` announces nothing — so it gets `network` instead.
   */
  spoken?: string;
  /**
   * The word's reading over the comparison period — "vs 1,100" and a change
   * badge in the readout. Absent with comparison off.
   */
  compare?: ChartTooltipComparison;
};

export type WordCloudProps = {
  words: readonly WordCloudWord[];
  /**
   * Plot height in px. Defaults to Figma's `Chart` slot. `'fill'` takes the
   * height of the slot instead, which is what a stretched `Card` row wants.
   */
  height?: number | 'fill';
  /** Formats a word's value in the readout. */
  formatValue?: (value: number) => string;
  /** What one unit is — `mentions`. A bare number beside a word says nothing. */
  unit?: string;
  /**
   * Keep only the largest N words. Unset draws them all.
   *
   * A cloud with too many words in it stops being one: every word drops toward
   * the bottom of the size scale and the shape the reader is here for goes with
   * it. See `Trending topics`, which caps at twelve of twenty.
   */
  limit?: number;
  /** Replaces the cloud with a loading message. */
  loading?: boolean;
  /** Replaces the cloud with an empty message. Worked out from the data if unset. */
  empty?: boolean;
  emptyLabel?: ReactNode;
  loadingLabel?: ReactNode;
  /** Accessible name. Derived from the words when omitted. */
  label?: string;
  className?: string;
  /**
   * Opens the detail behind one word — the conversations it was counted from.
   *
   * **The word itself is the target**, not an action inside the readout. Every
   * word is already a real `<button>` (see 3 above), so the affordance exists
   * and a second one nested inside its own tooltip would be invalid markup as
   * well as redundant. A cloud is the one chart here whose marks were already
   * clickable; it needed no anchoring to become so.
   */
  onViewDetails?: (key: string) => void;
};

/**
 * How many sizes a word can be drawn at.
 *
 * Five, and the stylesheet has exactly five rules to match — a sixth step here
 * would emit `data-size='5'`, which nothing matches, and the word would fall
 * back to the base size. The same coupling `HEAT_STEPS` documents.
 *
 * Five is also what the type scale can spend on this. The usable steps are
 * `xs` 10 → `display-xs` 32; `xxs` 8 is below what anybody reads at a glance,
 * and there is nothing above 32.
 */
export const WORD_SIZES = 5;

/**
 * Which size step a value lands on.
 *
 * **Linear in the value**, which is the plainest reading of the rule this
 * component exists to serve — "the more the volume of the word, the larger it
 * will be". Word-cloud convention would map *area* to value and so take a
 * square root; across five discrete steps the two differ by at most one step,
 * and the linear one is the one that can be explained in a sentence.
 *
 * The scale is pinned to the **largest word**, not to the range between the
 * largest and smallest. Normalising by the range would redraw the whole cloud
 * every time the filters moved — deselect the leader and the second word jumps
 * to the top size although nothing about it changed. Against a `0 … max`
 * domain, a word's size moves only when its own share of the leader does.
 */
function sizeStep(value: number, max: number): number {
  if (max <= 0) return 0;
  const t = Math.max(0, Math.min(1, value / max));
  /* `ceil`, so anything above zero clears the smallest step. */
  return Math.max(0, Math.min(WORD_SIZES - 1, Math.ceil(t * WORD_SIZES) - 1));
}

/**
 * Horizontal and vertical clearance between two placed words, in px.
 *
 * Asymmetric, and for the reason the flex gaps were: two words side by side
 * need clear water or they read as one phrase, while two words *stacked* are
 * already separated by the leading inside their own line boxes. Plain numbers
 * rather than tokens because they are arithmetic inside a layout loop — the
 * same rule chart geometry follows, since `var()` cannot be resolved in a
 * computation. They mirror `space/8` and `space/2`.
 */
const GAP_X = 8;
const GAP_Y = 2;

/** How far around the spiral one candidate is from the next, in radians. */
const SPIRAL_STEP = 0.25;

/** How fast the spiral opens, in px per radian. */
const SPIRAL_GROWTH = 1.6;

type Box = { left: number; top: number; right: number; bottom: number };

const hits = (a: Box, b: Box) =>
  a.left < b.right + GAP_X &&
  a.right + GAP_X > b.left &&
  a.top < b.bottom + GAP_Y &&
  a.bottom + GAP_Y > b.top;

/**
 * Where each word goes: a greedy spiral packing, largest word first.
 *
 * **This is what makes a cloud a cloud.** The layout was a `flex-wrap` until
 * 2026-09-26, and flex cannot produce this shape at all: every item on a line
 * shares a line box as tall as the biggest word on it, so a 10px word beside a
 * 32px one is fixed to the middle of a 32px band and the ~20px above and below
 * it can never be filled. Measured on `Trending topics`, fifteen words resolved
 * to five bands and covered 27% of the box. A packed cloud puts the small words
 * *in* those pockets, which needs each word placed at its own x and y and tested
 * against what is already down — a different algorithm, not a tighter flex.
 *
 * The method is d3-cloud's and wordcloud2's, minus the parts this does not
 * need: candidates are walked along an Archimedean spiral out from the centre
 * and the first one that collides with nothing already placed wins. No rotation
 * — every word stays horizontal, which is the reference the user gave and is
 * also what keeps a Bangla label legible. No pixel-level mask either: the
 * collision test is box against box, which packs less tightly than d3's sprite
 * mask but costs a few hundred comparisons instead of a canvas per word.
 *
 * The spiral is **stretched to the box's aspect ratio**, so in a 554x224 slot it
 * opens sideways roughly two and a half times faster than it opens downward.
 * A circular spiral in a letterbox fills a circle and leaves the ends empty.
 *
 * Deterministic: no randomness anywhere, so the same words at the same size
 * produce the same cloud on every render. That is what the seeded shuffle this
 * replaces existed to guarantee — and the shuffle itself is gone with the flex
 * layout that needed it. It was there because sorted flex rows stacked every
 * large word into the top row and read as a bar chart drawn in type; packing
 * mixes the sizes by construction, since each word simply takes the nearest
 * free space to the centre.
 *
 * A word that finds no free space inside the box is **dropped**, not clipped.
 * The box is fixed at the `Chart` slot's height and the caller's `limit` is a
 * guess at what fits, so overflow is a real case. A word half outside the cloud
 * reads as a rendering fault; one that is absent reads as a cloud of what fit,
 * which is what it is. The count is reported in the accessible name.
 */
function packed(
  boxes: readonly { key: string; width: number; height: number }[],
  width: number,
  height: number,
): Map<string, { x: number; y: number }> {
  const placed: Box[] = [];
  const out = new Map<string, { x: number; y: number }>();

  const cx = width / 2;
  const cy = height / 2;

  /* Past this the spiral is entirely outside the box, so nothing can fit. */
  const maxRadius = Math.hypot(width, height) / 2;
  /* `|| 1` for a zero-height box: the ratio is only a shape hint. */
  const aspect = height > 0 ? width / height : 1;

  for (const box of boxes) {
    if (box.width > width || box.height > height) continue;

    for (let t = 0; ; t += SPIRAL_STEP) {
      const radius = SPIRAL_GROWTH * t;
      if (radius > maxRadius) break;

      const x = cx + radius * aspect * Math.cos(t) - box.width / 2;
      const y = cy + radius * Math.sin(t) - box.height / 2;

      /* Inside the box, wholly — see the note on dropping above. */
      if (x < 0 || y < 0 || x + box.width > width || y + box.height > height) continue;

      const candidate = { left: x, top: y, right: x + box.width, bottom: y + box.height };
      if (placed.some((other) => hits(candidate, other))) continue;

      placed.push(candidate);
      out.set(box.key, { x: Math.round(x), y: Math.round(y) });
      break;
    }
  }

  return out;
}

export function WordCloud({
  words,
  height = 224,
  formatValue = (value) => String(Math.round(value)),
  unit = 'Value',
  limit,
  loading = false,
  empty,
  emptyLabel = 'No data',
  loadingLabel = 'Loading chart…',
  label,
  onViewDetails,
  className,
}: WordCloudProps) {
  const [hovered, setHovered] = useState<string | null>(null);

  /*
   * Where the readout sits relative to its word.
   *
   * `.cloud` clips what leaves it — it has to, or a word that overflows the box
   * would be drawn over the card's own chrome — and the readout is a child of a
   * word, so a bubble that hangs off any edge of the cloud is cut rather than
   * merely cramped. Reported 2026-09-26 on a top-row word, where the whole top
   * half of the bubble was gone.
   *
   * So the placement is measured rather than assumed: above the word unless
   * there is no room above it, and slid sideways by however much it takes to
   * keep both of its edges inside the cloud.
   *
   * **Why an offset and not `CalendarHeatmap`'s two edge alignments.** That
   * component pins the bubble to the cell's own edge, which works because a
   * heatmap cell is 8px wide and its first column starts at the grid's edge — a
   * bubble pinned to it is pinned to the grid. A cloud word is as wide as the
   * word, and the packer puts it wherever there was room, so pinning to the
   * word's edge is not pinning to anything: measured in the running app, the
   * first attempt still pushed 17 of 20 readouts out of the cloud, some by
   * 75px. The offset
   * below is the clamp itself, so there is no case it approximates.
   */
  const cloudRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const wordRefs = useRef(new Map<string, HTMLButtonElement>());
  const [placement, setPlacement] = useState<{ x: number; y: number } | null>(null);

  /*
   * Measured off the *word*, the *cloud* and the bubble's own size — never off
   * where the last placement put the bubble, which would oscillate between two
   * answers instead of settling on one. Both quantities it computes are
   * position-independent: a box's width does not depend on its left, and the
   * gap it needs above does not depend on whether it is currently above.
   */
  const place = useCallback(() => {
    const tip = tipRef.current;
    const cloud = cloudRef.current;
    const word = tip?.parentElement;
    if (!tip || !cloud || !word) return;

    /* `space/4`, the clearance `.tip` holds between bubble and word. */
    const gap = 4;
    const box = tip.getBoundingClientRect();
    const anchor = word.getBoundingClientRect();
    const bounds = cloud.getBoundingClientRect();

    /*
     * A bubble wider than the cloud cannot be slid anywhere useful, so the
     * cloud's width is published for `.tip` to cap itself against —
     * `ChartTooltip` runs to 296px and a cloud in a 384 card is narrower than
     * that. Written to the node rather than held in state: it is a measurement
     * of the layout, not a decision about it.
     */
    cloud.style.setProperty('--cloud-inline-size', `${bounds.width}px`);

    /* Where it would go if nothing were in the way: centred over the word. */
    const wantX = anchor.left + anchor.width / 2 - box.width / 2;

    /*
     * Above the word, and below it only when above does not fit and below does.
     * A bubble too tall for either side of its word stays above, where it loses
     * its last line to the clamp rather than its first.
     */
    const above = anchor.top - gap - box.height;
    const below = anchor.bottom + gap;
    const wantY = above < bounds.top && below + box.height <= bounds.bottom ? below : above;

    /*
     * ...and then pulled back inside the cloud on both axes. The clamp is the
     * whole of the collision handling: it covers the edges, and it also covers
     * a word the packing put half outside the box, where even the flip has
     * nothing to work with.
     */
    const clamp = (value: number, min: number, max: number) =>
      Math.round(Math.min(Math.max(value, min), Math.max(min, max)));

    const next = {
      x: clamp(wantX, bounds.left, bounds.right - box.width) - anchor.left,
      y: clamp(wantY, bounds.top, bounds.bottom - box.height) - anchor.top,
    };

    setPlacement((current) =>
      current && current.x === next.x && current.y === next.y ? current : next,
    );
  }, []);

  /*
   * Runs on every hover, before paint, so the corrected placement is the first
   * one drawn — the bubble never appears clipped and then jumps.
   */
  useLayoutEffect(() => {
    if (hovered === null) return;
    place();
  }, [hovered, place]);

  /*
   * Ranked and capped — the ranking is what `limit` cuts against and what sizes
   * every word, so it is computed whole before anything is dropped or moved.
   */
  const drawn = useMemo(() => {
    const ranked = [...words].filter((word) => word.value > 0).sort((a, b) => b.value - a.value);
    return limit === undefined ? ranked : ranked.slice(0, limit);
  }, [words, limit]);

  const max = drawn[0]?.value ?? 0;

  /*
   * Nothing to draw is empty, and the test is the total rather than `isEmpty`'s
   * per-series one: `isEmpty` treats `0` as a reading, which on a line is right
   * — a Tuesday the filter emptied still has to be drawn. A word with no
   * mentions is not a small word, it is a word that is not in the period at
   * all, so it is filtered out above and a cloud of none is blank.
   */
  const state = loading ? 'loading' : (empty ?? drawn.length === 0) ? 'empty' : 'cloud';

  /*
   * Where each word sits, or `null` before the first measurement.
   *
   * The pass is measure-then-place: the words are rendered once at their real
   * size, their boxes are read off the DOM, `packed` decides the positions, and
   * a second render applies them. Measuring the rendered type rather than
   * predicting it with `canvas.measureText` is what keeps the packing honest —
   * the boxes include this system's own `letter-spacing`, the per-step
   * `line-height`, and whatever font the browser actually resolved for a Bangla
   * label, none of which a font-string guess would carry.
   *
   * `null` also means "measure again", which is how a resize is handled.
   */
  const [layout, setLayout] = useState<Map<string, { x: number; y: number }> | null>(null);

  /*
   * The latest words, for `measure` to read without being rebuilt when they
   * change identity. A caller builds `words` fresh on every render, so a
   * `useCallback` that closed over `drawn` would get a new identity every
   * render, and an effect depending on it would re-measure every render —
   * which sets a new `Map`, which renders again. The ref keeps `measure`
   * stable and the effect below fires on the signature instead.
   */
  const drawnRef = useRef(drawn);
  drawnRef.current = drawn;

  const measure = useCallback(() => {
    const cloud = cloudRef.current;
    if (!cloud) return;

    const bounds = cloud.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) return;

    /*
     * Largest first, which is the order `drawn` is already in, and the order
     * that matters: the spiral hands out the space nearest the centre first, so
     * placing the leader first is what puts it in the middle and leaves the
     * pockets around it for the small words.
     */
    const boxes = drawnRef.current.flatMap((word) => {
      const node = wordRefs.current.get(word.key);
      if (!node) return [];
      /*
       * `getBoundingClientRect`, not `offsetWidth`: the latter rounds to whole
       * px, and a packing that rounds every box down by up to half a pixel
       * accumulates that error into overlaps between neighbours.
       *
       * A word the last pass dropped is `hidden`, and a hidden word measures
       * 0×0 — which fits anywhere, so the packer placed it on top of another
       * word and it reappeared at full size over it (2026-10-27, GP's 27
       * topics in a four-track card: seven overlaps after a second pass). It
       * is shown for the read and hidden again before anything paints.
       */
      const wasHidden = node.hidden;
      node.hidden = false;
      const rect = node.getBoundingClientRect();
      node.hidden = wasHidden;
      return [{ key: word.key, width: rect.width, height: rect.height }];
    });

    setLayout(packed(boxes, bounds.width, bounds.height));
  }, []);

  /**
   * Measure and place, before paint — so a cloud is never painted unplaced.
   *
   * **One effect, deliberately.** The first cut had two: one that placed when
   * the layout was `null`, and one that reset to `null` when the words changed.
   * Both ran in the same commit, React batched the two updates, and `null` →
   * map → `null` is no change at all — so it bailed out of re-rendering and
   * every cloud stayed in its measuring state with all fifteen words stacked at
   * the origin. Two effects writing one piece of state in one commit is the bug,
   * not the ordering between them.
   *
   * The signature is the words themselves rather than the array's identity, for
   * the reason `drawnRef` exists: a caller rebuilds `words` on every render.
   */
  const signature = drawn.map((word) => `${word.key}:${word.value}`).join('|');

  useLayoutEffect(() => {
    measure();
  }, [signature, measure]);

  /**
   * Measure again once the webfont has loaded.
   *
   * **This is not belt and braces — without it the packing is wrong on first
   * paint.** The first measurement can run before Inter has swapped in, so
   * every box is the fallback font's width, and the cloud is packed against
   * boxes that are not the ones it will draw. Measured on `Trending topics`:
   * `Data packs` measured 110px against the 122px it renders at, and three
   * pairs of words ended up overlapping by 3–14px.
   *
   * `document.fonts.ready` resolves once, after every pending face has loaded,
   * so this costs one extra pass and only on the first cloud of a session. The
   * guard is for a stale resolve arriving after unmount.
   */
  useLayoutEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;

    let live = true;
    document.fonts.ready.then(() => {
      if (live) measure();
    });
    return () => {
      live = false;
    };
  }, [measure]);

  /*
   * The packing is computed against the box's width and height, so it has to be
   * recomputed when either changes — a card in a narrower column would
   * otherwise keep positions packed for the wider one, and the packer's
   * guarantee that every word is inside the box would no longer hold.
   */
  useLayoutEffect(() => {
    const cloud = cloudRef.current;
    if (!cloud || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(cloud);
    return () => observer.disconnect();
  }, [state, measure]);

  const fill = height === 'fill';

  /*
   * The cloud's whole reading, in one sentence, because the marks themselves
   * are buttons a screen reader steps through one at a time — without this a
   * listener gets fifteen words and no statement of what the size or the colour
   * of any of them meant.
   */
  const leader = drawn[0];

  /*
   * How many words the box had room for. Said out loud when it is fewer than
   * were handed in: a reader who can see the cloud can see that some words are
   * missing only by knowing what should be there, and a listener cannot tell at
   * all. Silence would make the cloud claim to be the whole ranking.
   */
  const shown = layout === null ? drawn.length : layout.size;

  const name =
    label ??
    `${unit} by word, sized by volume and coloured by sentiment.` +
      (leader ? ` Largest: ${leader.spoken ?? leader.label}, ${formatValue(leader.value)}.` : '') +
      (shown < drawn.length ? ` Showing the ${shown} that fit, of ${drawn.length}.` : '');

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div
        className={[styles.plot, fill && styles.fillPlot].filter(Boolean).join(' ')}
        style={fill ? undefined : { height }}
        role={onViewDetails ? 'group' : 'img'}
        aria-label={name}
        aria-busy={loading || undefined}
      >
        {state === 'cloud' ? (
          <div
            ref={cloudRef}
            className={styles.cloud}
            /* The stylesheet hides the words while this is on — see `.cloud`. */
            data-measuring={layout === null || undefined}
            onPointerLeave={() => setHovered(null)}
          >
            {drawn.map((word) => {
              const on = hovered === word.key;
              const spoken = word.spoken ?? word.label;
              const at = layout?.get(word.key);

              /*
               * Placed, but nowhere — `packed` found no free space inside the
               * box for it. Dropped rather than clipped, and dropped from the
               * accessibility tree with it: a word nobody can see should not be
               * a tab stop, and `hidden` is the one attribute that says both.
               * It stays mounted so the next measuring pass, after a resize,
               * can put it back.
               */
              const dropped = layout !== null && at === undefined;

              return (
                <button
                  key={word.key}
                  ref={(node) => {
                    if (node) wordRefs.current.set(word.key, node);
                    else wordRefs.current.delete(word.key);
                  }}
                  hidden={dropped || undefined}
                  type="button"
                  className={[styles.word, word.color ? null : styles.untoned]
                    .filter(Boolean)
                    .join(' ')}
                  data-size={sizeStep(word.value, max)}
                  /*
                   * The tone colour is the one value that cannot live in the
                   * stylesheet: which of the three a word takes is decided per
                   * word, at render, from its own mention mix. It is still a
                   * token — `TokenName` resolves to a `var(--chart-N)`, so no
                   * hex can be written here.
                   */
                  /*
                   * Two things, and only one of them is a token. The tone is:
                   * which of the three a word takes is decided per word, at
                   * render, from its own mention mix, and `TokenName` resolves
                   * to a `var(--chart-N)` so no hex can be written here. The
                   * position is not, and cannot be — it is the packer's own
                   * arithmetic, in px, the same reason chart geometry is a
                   * number rather than a custom property.
                   */
                  style={
                    {
                      ...(word.color ? { '--word-tone': token[word.color] } : null),
                      ...(at ? { left: `${at.x}px`, top: `${at.y}px` } : null),
                    } as CSSProperties
                  }
                  onPointerEnter={() => setHovered(word.key)}
                  onFocus={() => setHovered(word.key)}
                  onBlur={() => setHovered(null)}
                  onClick={onViewDetails && (() => onViewDetails(word.key))}
                  /*
                   * Not in Figma, which has no way to say anything that is not
                   * drawn. The word on screen is the value's *label*; the value,
                   * the tone and the share behind it are only in the readout, so
                   * they go in the name too or a screen reader gets a list of
                   * words with no measurement attached to any of them.
                   */
                  aria-label={[spoken, `${formatValue(word.value)} ${unit}`, word.tone, word.detail]
                    .filter(Boolean)
                    .join(', ')}
                >
                  <span aria-hidden="true">{word.label}</span>
                  {on && (
                    <span
                      ref={tipRef}
                      className={styles.tip}
                      /*
                       * The measured placement, once there is one. Until then
                       * the stylesheet's centred default above the word, which
                       * is the position that gets measured. The margin goes
                       * with it: the gap is inside these coordinates.
                       */
                      style={
                        placement === null
                          ? undefined
                          : {
                              insetInlineStart: `${placement.x}px`,
                              insetBlockStart: `${placement.y}px`,
                              insetBlockEnd: 'auto',
                              margin: 0,
                              transform: 'none',
                            }
                      }
                      aria-hidden="true"
                    >
                      <ChartTooltip
                        active
                        label={word.label}
                        payload={[
                          {
                            /*
                             * The tone and the share behind it read as one
                             * phrase — `Positive · 62% of mentions` — rather
                             * than as two rows. A second row would need a
                             * second swatch, and there is only one colour on
                             * this mark to put in it.
                             */
                            name: [word.tone, word.detail].filter(Boolean).join(' · ') || unit,
                            value: `${formatValue(word.value)} ${unit}`,
                            color: word.color ? token[word.color] : token['text-tertiary'],
                          },
                        ]}
                        compare={word.compare && (() => word.compare)}
                      />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className={styles.message}>{state === 'loading' ? loadingLabel : emptyLabel}</p>
        )}
      </div>
    </div>
  );
}
